// The default behaviour of RTK query is having a baseQuery which controls how requests are sent (fetch, axios,...)
// And then a query for each endpoint to specify which path to call with which method and data
// The problem with this is that you loose type information from the generated openapi client
// As a workaround, we can use queryFn which totally bypassed the baseQuery.

import Bugsnag from '@bugsnag/js';
import { AxiosError, AxiosResponse } from 'axios';

// From @reduxjs/toolkit/dist/query/baseQueryTypes
type QueryReturnValue<T = unknown, E = unknown, M = unknown> =
  | {
    error: E;
    data?: undefined;
    meta?: M;
  }
  | {
    error?: undefined;
    data: T;
    meta?: M;
  };

export interface BackendErrorType {
  message: string;
  code?: string;
  meta?: any;
  httpStatusCode?: number;
}

/**
 * Handles errors from API calls and standardizes the return format for RTK Query
 * 
 * - If result directly contains an error object like { error: {...} }, returns it as an error
 * - If result is {data: T}, returns it directly
 * - If result is an AxiosResponse<T>, unwraps the data property
 * - If result has {data: {error: ...}}, extracts the nested error
 * - For AxiosErrors, unwraps and formats the error appropriately
 * - For generic errors, extracts message if available
 * - Reports errors without a code to Bugsnag
 * 
 * The function automatically unwraps Axios responses, so a function returning AxiosResponse<T>
 * will have its result converted to the expected QueryReturnValue<T, BackendErrorType, {}>.
 * 
 * @param func The function to execute and handle errors for
 * @returns A properly typed QueryReturnValue with either data or standardized error
 */
export async function handleErrors<T>(
  func: () => Promise<T | AxiosResponse<T> | { error: BackendErrorType } | { data: T }>
): Promise<QueryReturnValue<T, BackendErrorType, {}>> {
  try {
    const res = await func();

    // If response has an error property, unwrap, normalize and it and return it
    if (res && typeof res === 'object' && 'error' in res && res.error) {
      return {
        error: normalizeError(res.error)
      };
    }

    // If the response has a data property (e.g. when handling axios responses), unwrap and return it
    if (res && typeof res === 'object' && 'data' in res) {
      return { data: res.data };
    }

    // Otherwise just return the result directly
    return { data: res as T };
  } catch (error) {
    // Handle Axios errors
    if (error instanceof AxiosError) {
      const axiosError = error as AxiosError;

      // Try to extract structured error data from response
      if (axiosError.response?.data) {
        const responseData = axiosError.response.data;

        return {
          error: { ...normalizeError(responseData), httpStatusCode: axiosError.response?.status }
        };
      }
      else {
        return {
          error: {
            code: axiosError.code ?? 'UNKNOWN_ERROR',
            message: axiosError.message || 'An unknown error occurred',
            httpStatusCode: axiosError.response?.status
          }
        };
      }
    }
    // Handle generic errors
    if (error instanceof Error) {
      Bugsnag.notify(error);
      return {
        error: {
          message: error.message || 'An unknown error occurred',
          code: 'UNKNOWN_ERROR'
        }
      };
    }

    // Handle any type of error that does not even have an error type
    Bugsnag.notify(new Error(`Unknown error: ${JSON.stringify(error)}`));
    return {
      error: {
        message: 'An unknown error occurred',
        code: 'UNKNOWN_ERROR'
      }
    };
  }
}

/**
 * Normalizes various error formats into the standard BackendErrorType
 */
function normalizeError(error: any): BackendErrorType {
  // If already in the expected format, return it
  if (error && typeof error === 'object' && 'message' in error) {
    return {
      message: error.message,
      code: error.code
    };
  }

  // If it has a code but no message, add a generic message
  if (error && typeof error === 'object' && 'code' in error) {
    return {
      message: error.message || `Error occurred, code: ${error.code}`,
      code: error.code
    };
  }

  // If it's a string, use it as the message
  if (typeof error === 'string') {
    Bugsnag.notify(new Error(error));
    return {
      message: error,
    };
  }

  // For other cases, report to bugsnag and return a generic error
  Bugsnag.notify(new Error(`Unknown error: ${JSON.stringify(error)}`));
  return {
    message: 'An unknown error occurred',
    code: 'UNKNOWN_ERROR'
  };
}